/* eslint-disable unicorn/filename-case */
/* eslint-disable max-lines */
import {
  Link,
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';
import { Lightbulb02, RefreshCw05 } from '@untitled-ui/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { BannerCallout } from '../../../components/banner-callout';
import { ButtonSeeHowItWorks } from '../../../components/button-see-how-it-works';
import { Button } from '../../../components/buttons/button';
import { FeaturedIcon } from '../../../components/featured-icon';
import { PlayCircleIcon } from '../../../components/icons/play-circle-icon';
import { ModalRefreshAudit } from '../../../components/modal-refresh-audit';
import { ModalYoutubeEmbed } from '../../../components/modal-youtube-embed';
import { PageBlockerSubscription } from '../../../components/page-blocker';
import { TabButton } from '../../../components/tabs';
import { ToastVariant } from '../../../components/toast';
import { TrialSubscriptionModal } from '../../../components/trial-subscription-modal';
import type { GetSeoStrategyByBrandIdRes } from '../../../entity/api/routes/seo-strategy';
import { SeoStrategyStatus } from '../../../entity/api/seo-strategy';
import { ForbiddenError } from '../../../error';
import { convertError } from '../../../error/convert';
import { addTrackCrumb } from '../../../error/tracking';
import { getQueryClient } from '../../../sdk/request';
import { PostHogEvents, capturePostHogEvent } from '../../../services/posthog';
import {
  invalidateOrganizationBilling,
  useAuthUser,
  useCurrentOrgUserRole,
  useOrganizationBilling,
} from '../../../services/storage';
import {
  useSaveSeoStrategy,
  useSeoStrategyByBrandId,
} from '../../../services/storage/seo-strategy-queries';
import { tGlobal, tNamespace } from '../../../services/translation';
import { getSelectedBrand } from '../../../shared/brand';
import { useToast } from '../../../shared/contexts/toast-context';
import { Feature, isFeatureAvailable } from '../../../shared/feature';
import { canOrgGenerateAudit, getSelectedOrganization } from '../../../shared/organization';
import { isNotSubscribed } from '../../../shared/subscription';
import { currentPathIsActive } from '../../../shared/url';
import { BrandNotFilledModal } from '../_sidebar-layout.blogs/-brand-not-filled-modal';
import { SetupLoading } from './-setup-loading';
import { type SetupFormData, SetupSeoModal } from './-setup-seo-modal';

const t = tNamespace('app.seo-strategy');
const tErrors = tNamespace('errors');

type PageState = 'hero' | 'loading' | 'dashboard';

function computePageState(
  seoStrategy: GetSeoStrategyByBrandIdRes['data'] | undefined,
  isMutationPending: boolean
): PageState {
  if (isMutationPending || seoStrategy?.strategy?.status === SeoStrategyStatus.Pending) {
    return 'loading';
  }
  if (seoStrategy?.strategy?.sumOverview == null || seoStrategy?.strategy?.sumOverview == '') {
    return 'hero';
  }
  return 'dashboard';
}

export const Route = createFileRoute('/app/_sidebar-layout/seo-strategy/$brandId')({
  beforeLoad: function () {
    if (!isFeatureAvailable(Feature.AUDIT)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/app', replace: true });
    }
    addTrackCrumb({
      category: 'route',
      message: '/app/_sidebar-layout/seo-strategy/$brandId',
      level: 'info',
    });
  },
  // eslint-disable-next-line no-use-before-define
  component: SeoStrategyPage,
});

function SeoStrategyPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const addToast = useToast();

  const { brandId } = Route.useParams();
  const { data: currentUser } = useAuthUser();

  const currentOrg = getSelectedOrganization(currentUser);
  const { data: orgBilling } = useOrganizationBilling(currentOrg?._id);
  const { currentUserOrganization } = useCurrentOrgUserRole(
    currentOrg?._id ?? '',
    currentUser?._id
  );
  const currentBrand = getSelectedBrand(currentUser);
  const { data: seoStrategy } = useSeoStrategyByBrandId(brandId);
  const saveMutation = useSaveSeoStrategy();
  const pageState = computePageState(seoStrategy, saveMutation.isPending);
  const wasInLoadingState = useRef(false);

  useEffect(
    function () {
      if (pageState === 'loading') {
        wasInLoadingState.current = true;
        return;
      }
      if (pageState === 'dashboard' && wasInLoadingState.current) {
        wasInLoadingState.current = false;
        navigate({
          to: '/app/seo-strategy/$brandId/overview',
          params: { brandId },
          replace: true,
        }).catch(function () {});
      }
    },
    [pageState, navigate, brandId]
  );

  const [brandContextModalOpen, setBrandContextModalOpen] = useState(false);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [setupSeoModalOpen, setSetupSeoModalOpen] = useState(false);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  const isBrandFilled = currentBrand?.excerpt != null && currentBrand?.excerpt?.length > 0;

  const handleSetupClick = useCallback(
    function () {
      if (isNotSubscribed(orgBilling) && !canOrgGenerateAudit(orgBilling)) {
        setSubscriptionModalOpen(true);
        return;
      }

      if (!isBrandFilled) {
        setBrandContextModalOpen(true);
        return;
      }

      setSetupSeoModalOpen(true);
    },
    [isBrandFilled, orgBilling]
  );

  function handleRefreshClick() {
    if (seoStrategy?.strategy == null || currentOrg == null) {
      return;
    }

    if (isNotSubscribed(orgBilling) && !canOrgGenerateAudit(orgBilling)) {
      setSubscriptionModalOpen(true);
      return;
    }

    setRefreshModalOpen(true);
  }

  function handleGoToBrandContext() {
    setBrandContextModalOpen(false);
    navigate({
      to: '/app/brand-context',
      search: { brandId },
    }).catch(function () {});
  }

  function handleSetupSubmit(data: SetupFormData) {
    if (currentOrg == null) {
      return;
    }

    /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
    const payload = {
      brandId,
      organizationId: currentOrg._id,
      country: data.targetCountry.toUpperCase(),
      language: data.language,
      websiteUrl: data.websiteUrl,
      competitorsUrls: data.competitors.filter(function (c: string) {
        return c.trim() !== '';
      }),
      productsServices: data.products,
      problemsSolved: data.painPoints,
      faqSales: data.salesQuestions,
      purchaseBarriers: data.barriers,
      additionalNotes: data.additionalInfo,
      pagesPerMonth: data.pagesPerMonth,
    };
    /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */

    saveMutation.mutate(
      { brandId, data: payload },
      {
        onError: function (err) {
          const handledErr = convertError(err);
          // handle forbiddens (which comes from plan features limitations)
          // as such, the original message is a specific enum incoming
          if (handledErr instanceof ForbiddenError) {
            addToast({
              variant: ToastVariant.Error,
              title: tErrors(handledErr.originalMessage),
              id: Date.now(),
            });
            return;
          }

          addToast({
            title: tGlobal('somethingWentWrong'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
        },
        onSuccess: function () {
          capturePostHogEvent(PostHogEvents.SEO_AUDIT_RUN, {
            urlAudited: payload.websiteUrl,
          });

          const queryClient = getQueryClient();
          invalidateOrganizationBilling(queryClient, currentOrg._id);
        },
      }
    );
  }

  if (pageState === 'loading') {
    return (
      <div className="flex h-full flex-col">
        <SetupLoading />
      </div>
    );
  }

  if (pageState === 'dashboard') {
    return (
      <div className="relative flex h-full min-h-0 flex-col">
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="mx-auto w-full max-w-[800px] p-4 pt-12 md:px-0 md:pb-0 md:pt-12">
            {/* Page header */}
            <div className="mb-6 flex items-start justify-between">
              <div>
                <p className="font-display-2 text-black">{t('pageTitle')}</p>
                <p className="font-body-2-r text-branding-gray">{t('dashboardSubtitle')}</p>
                <ButtonSeeHowItWorks videoUrl="https://www.youtube.com/embed/70z1_j2UFDA" />
              </div>
              <Button
                variant="outline"
                size="md"
                leftIcon={<RefreshCw05 width={16} height={16} />}
                disabled={seoStrategy?.strategy == null}
                onClick={handleRefreshClick}
              >
                {t('refreshAudit')}
              </Button>
            </div>

            {/* Tab list */}
            <div className="flex w-fit overflow-x-auto border-b border-branding-gray-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <Link to="/app/seo-strategy/$brandId/overview" params={{ brandId }}>
                <TabButton
                  value="overview"
                  title={t('tabOverview')}
                  asDiv={true}
                  dataState={
                    currentPathIsActive(`/app/seo-strategy/${brandId}/overview`, pathname)
                      ? 'active'
                      : 'inactive'
                  }
                  className="px-2"
                />
              </Link>
              <Link to="/app/seo-strategy/$brandId/keywords" params={{ brandId }}>
                <TabButton
                  value="keywords"
                  title={t('tabKeywords')}
                  asDiv={true}
                  dataState={
                    currentPathIsActive(`/app/seo-strategy/${brandId}/keywords`, pathname)
                      ? 'active'
                      : 'inactive'
                  }
                  className="px-2"
                />
              </Link>
              <Link to="/app/seo-strategy/$brandId/action-plan" params={{ brandId }}>
                <TabButton
                  value="action-plan"
                  title={t('tabActionPlan')}
                  asDiv={true}
                  dataState={
                    currentPathIsActive(`/app/seo-strategy/${brandId}/action-plan`, pathname)
                      ? 'active'
                      : 'inactive'
                  }
                  className="px-2"
                />
              </Link>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <Outlet />
          </div>
        </div>
        {seoStrategy?.strategy != null && currentOrg != null && (
          <ModalRefreshAudit
            open={refreshModalOpen}
            onOpenChange={function (open) {
              setRefreshModalOpen(open);
            }}
            brandId={brandId}
            organizationId={currentOrg._id}
            strategy={seoStrategy.strategy}
          />
        )}
        <TrialSubscriptionModal
          open={subscriptionModalOpen}
          onOpenChange={setSubscriptionModalOpen}
          orgId={currentOrg?._id ?? ''}
        />

        <PageBlockerSubscription
          organizationUser={currentUserOrganization}
          organizationBilling={orgBilling}
          isFromSeoStrategy
        />
      </div>
    );
  }

  // Hero state
  return (
    <div className="relative flex h-full flex-col justify-between p-4 md:px-0 md:pb-12 md:pt-12">
      <div className="mx-auto flex w-full max-w-[800px] flex-col">
        <div className="mb-8 flex items-center justify-between md:mb-16">
          <p className="font-display-2 mb-8 text-black">{t('pageTitle')}</p>
        </div>

        {/* Hero card */}
        <div className="flex h-[200px] flex-col items-center gap-8 overflow-hidden rounded-lg bg-branding-accent-50">
          <img
            src="/onboarding_3.png"
            alt="SEO strategy illustration"
            className="h-full object-contain"
            onError={function (e) {
              const el = e.currentTarget as HTMLImageElement;
              el.style.display = 'none';
            }}
          />
        </div>

        {/* Text + CTA */}
        <div className="mt-16 flex flex-col items-center text-center">
          <p className="font-display-2 mb-4 text-black">{t('heroTitle')}</p>
          <p className="font-body-2-r mb-8 max-w-[491px] text-branding-gray-600">
            {t('heroSubtitle')}
          </p>
          <Button variant="accent" size="xl" onClick={handleSetupClick}>
            {t('heroCta')}
          </Button>
        </div>
      </div>

      {/* Bottom callout banner — pinned to the bottom of the flex column */}
      <div className="mx-auto w-full max-w-[800px]">
        <BannerCallout
          icon={
            <FeaturedIcon icon={<Lightbulb02 width={16} height={16} />} color="accent" size="sm" />
          }
          content={
            <>
              <span className="font-body-2-sb bg-gradient-to-r from-[#535CDC] to-[#4117B2] bg-clip-text text-transparent">
                {tGlobal('newHere')}
              </span>{' '}
              <span className="font-body-2-sb text-dark-gray">{t('heroCalloutWatch')}</span>
            </>
          }
          action={
            <Button
              variant="outline"
              size="sm"
              leftIcon={<PlayCircleIcon />}
              onClick={function () {
                setIsVideoOpen(true);
              }}
            >
              {tGlobal('seeHowItWorks')}
            </Button>
          }
        />
        <ModalYoutubeEmbed
          isOpen={isVideoOpen}
          onClose={function () {
            setIsVideoOpen(false);
          }}
          videoUrl="https://www.youtube.com/embed/boF2wvei-Tw"
        />
      </div>

      {/* Modals */}
      <BrandNotFilledModal
        open={brandContextModalOpen}
        onOpenChange={setBrandContextModalOpen}
        createAnyway={function () {
          setSetupSeoModalOpen(true);
          setBrandContextModalOpen(false);
        }}
        addBrandContext={handleGoToBrandContext}
      />

      <TrialSubscriptionModal
        open={subscriptionModalOpen}
        onOpenChange={setSubscriptionModalOpen}
        orgId={currentOrg?._id ?? ''}
      />

      <SetupSeoModal
        open={setupSeoModalOpen}
        onOpenChange={setSetupSeoModalOpen}
        onSubmit={handleSetupSubmit}
        brand={currentBrand}
      />

      <PageBlockerSubscription
        organizationUser={currentUserOrganization}
        organizationBilling={orgBilling}
        isFromSeoStrategy
      />
    </div>
  );
}
